import { Box, Stack, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { CustomTabs } from "../../components/CustomTabs";
import { PageLayout } from "../../components/PageLayout";
import { useAllContentMetadata } from "../../hooks/useContentMetadata";
import { useLocalization } from "../../hooks/useLocalization";
import { type TabItem, useTabNavigation } from "../../hooks/useTabNavigation";
import { EExperienceTabs } from "../../types/NavigationPaths";
import { ExperienceGroupsTab } from "./components/tabs/ExperienceGroupsTab";
import { ExperienceInfo } from "./components/tabs/ExperienceOverviewTab";
import { ExperienceUsersTab } from "./components/tabs/ExperienceUsersTab";
import { useExperienceById } from "./hooks/useExperienceById";
import { useExperienceGroups } from "./hooks/useExperienceGroups";
import { useExperienceUsers } from "./hooks/useExperienceUsers";
import { useExternalExperienceGroups } from "./hooks/useExternalExperienceGroups";
import { useExternalExperienceUsers } from "./hooks/useExternalExperienceUsers";
import { useIncomingLicenses } from "./hooks/useIncomingLicenses";
import { computeNextRenewalDate, getContentMetadataId, ymd } from "./utils";

export const ExperienceDetailsPage: React.FC = () => {
  const { t } = useLocalization();
  const theme = useTheme();
  const { exp, loading } = useExperienceById();
  const { companyId } = useParams();

  const { licensesByReleaseChannel, isLoading: licenseLoading } =
    useIncomingLicenses();

  const isInternal = useMemo(
    () => exp?.companyId === companyId,
    [exp?.companyId, companyId],
  );

  const license = useMemo(() => {
    if (!exp?.id || isInternal) return null;

    return licensesByReleaseChannel.get(exp.id) ?? null;
  }, [exp?.id, isInternal, licensesByReleaseChannel]);

  const showGroupsTab = !exp?.isExternalLimited && !license?.hasWhitelist;

  const { rows: userRows, loading: usersLoading } = useExperienceUsers({
    releaseChannelId: exp?.id,
    companyId: companyId,
    enabled: !!exp?.id && isInternal,
  });

  const { rows: externalUserRows, loading: externalUsersLoading } =
    useExternalExperienceUsers({
      releaseChannelId: exp?.id,
      companyId: companyId,
      enabled: !!exp?.id && !isInternal,
    });

  const rows = useMemo(() => {
    const rawRows = isInternal ? userRows : externalUserRows;

    return rawRows.filter((row) => {
      const isUnknown = row.fullName === "Unknown user";

      const isMissingEmail = !row.email || row.email.trim() === "";

      return !isUnknown && !isMissingEmail;
    });
  }, [isInternal, userRows, externalUserRows]);

  const { rows: groupRows, loading: groupsLoading } = useExperienceGroups({
    releaseChannelId: exp?.id,
    companyId: companyId,
    enabled: !!exp?.id && isInternal,
  });

  const { rows: externalGroupRows, loading: externalGroupsLoading } =
    useExternalExperienceGroups({
      releaseChannelId: exp?.id,
      companyId: companyId,
      enabled: !!exp?.id && !isInternal && showGroupsTab,
    });

  const groupRowsToDisplay = useMemo(() => {
    return isInternal ? groupRows : externalGroupRows;
  }, [isInternal, groupRows, externalGroupRows]);

  const tabLabels = useMemo(
    () => ({
      [EExperienceTabs.OVERVIEW]: t("experiences.tabs.overview"),
      [EExperienceTabs.USERS]: t("experiences.tabs.users"),
      [EExperienceTabs.GROUPS]: t("experiences.tabs.groups"),
    }),
    [t],
  );

  const { activeTab, handleTabChange, tabItems } =
    useTabNavigation<EExperienceTabs>({
      tabEnum: EExperienceTabs,
      defaultTab: EExperienceTabs.OVERVIEW,
      labels: tabLabels,
    });

  const filteredTabs = React.useMemo<TabItem<EExperienceTabs>[]>(
    () =>
      showGroupsTab
        ? tabItems
        : tabItems.filter((tab) => tab.value !== EExperienceTabs.GROUPS),
    [showGroupsTab, tabItems],
  );

  const { contentMetadataMap } = useAllContentMetadata({ enabled: true });

  const contentMetadataId = React.useMemo(
    () => getContentMetadataId(exp),
    [exp],
  );

  const meta = React.useMemo(
    () =>
      contentMetadataId ? contentMetadataMap.get(contentMetadataId) : undefined,
    [contentMetadataId, contentMetadataMap],
  );

  const readMetaStr = (m: unknown, ...keys: string[]): string | undefined => {
    if (!m || typeof m !== "object") return undefined;
    const r = m as Record<string, unknown>;
    for (const k of keys) {
      const v = r[k];
      if (typeof v === "string" && v.trim()) return v;
    }

    return undefined;
  };

  const experienceName =
    readMetaStr(meta, "friendlyName", "FriendlyName") ?? exp?.name ?? "";

  const description =
    readMetaStr(meta, "description", "Description") ?? exp?.description ?? null;

  React.useEffect(() => {
    if (!showGroupsTab && activeTab === EExperienceTabs.GROUPS) {
      handleTabChange({} as React.SyntheticEvent, EExperienceTabs.OVERVIEW);
    }
  }, [showGroupsTab, activeTab, handleTabChange]);

  if (loading) {
    return (
      <PageLayout isLoading maxWidth={false}>
        {null}
      </PageLayout>
    );
  }

  if (!exp) {
    return (
      <PageLayout isLoading={false} maxWidth={false}>
        <Typography sx={{ p: 3, color: theme.colors.text[3] }}>
          {t("experiences.experienceNotFound")}
        </Typography>
      </PageLayout>
    );
  }

  const renewalDate = computeNextRenewalDate(exp.licenseStartDate ?? null);
  const expiration = exp.expiresAt ?? null;

  const isExpirationAfterRenewal = expiration
    ? expiration.getTime() > renewalDate.getTime()
    : false;

  const showRenewalOnBlocked = Boolean(
    exp.isExternalLimited && isExpirationAfterRenewal,
  );

  const totalSeats = exp.seats.total ?? null;

  const consumed = exp.seats.used;

  const blocked = exp.isExternalLimited
    ? consumed - externalUserRows.length
    : 0;

  return (
    <PageLayout isLoading={false}>
      <Stack spacing={{ xs: 1, md: 2 }} sx={{ width: "100%", minWidth: 0 }}>
        {/* Title */}
        <Box sx={{ height: 48, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 18, md: 24 },
              color: theme.colors.text[5],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {exp.name}
          </Typography>
        </Box>

        <CustomTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={filteredTabs}
        />
        <Box sx={{ paddingTop: { xs: 2, md: 0 } }}>
          {activeTab === EExperienceTabs.OVERVIEW && (
            <ExperienceInfo
              blocked={blocked}
              companyName={exp.companyName}
              consumed={consumed}
              description={description}
              experienceName={experienceName}
              expiresAt={exp.expiresAt ?? null}
              imageUrl={exp.imageUrl}
              isExternalLimited={!!exp.isExternalLimited}
              renewalDateISO={ymd(renewalDate)}
              showRenewalOnBlocked={showRenewalOnBlocked}
              totalSeats={totalSeats}
              version={exp.version}
            />
          )}

          {activeTab === EExperienceTabs.USERS && (
            <ExperienceUsersTab
              experienceName={experienceName}
              isExternal={!isInternal}
              isLoading={usersLoading || externalUsersLoading || licenseLoading}
              licenseExpiresAt={exp.expiresAt ?? null}
              licenseId={license?.licenseId}
              releaseChannelId={exp.id}
              rows={rows}
            />
          )}

          {activeTab === EExperienceTabs.GROUPS && showGroupsTab && (
            <ExperienceGroupsTab
              experienceName={experienceName}
              isExternal={!isInternal}
              isLoading={
                groupsLoading || externalGroupsLoading || licenseLoading
              }
              licenseExpiresAt={exp.expiresAt ?? null}
              licenseId={license?.licenseId}
              releaseChannelId={exp.id}
              rows={groupRowsToDisplay}
            />
          )}
        </Box>
      </Stack>
    </PageLayout>
  );
};
